import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { rd } from '@passionware/monads';
import { Building2, FileText, MoreHorizontal, Trash2 } from 'lucide-react';

import { Capability } from '__types__';
import { openModal } from 'store/v1/ui/ui.actions.js';
import useModal from 'hooks/useModal';
import GenerateResourcePDFModal from 'modals/ResourceModals/GenerateResourcePDFModal/GenerateResourcePDFModal';
import {
  RestrictedDropdownItem,
  RestrictedDropdownV5
} from '../../../buttons/RestrictedDropdown/RestrictedDropdown.jsx';
import { Button } from 'v5/design-system/Button.js';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu.js';
import { myServices } from 'v5/services/services.connected.app';
import { selectAppId } from 'store/v1/auth/auth.selectors';
import { useTranslation } from 'react-i18next';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';

const ResourceOptions = ({
  resourceId,
  excludeOptions = [],
  buttonVariant = 'secondary'
}) => {
  const dispatch = useDispatch();
  const resources = useSelector(state => state.contacts);
  const resource = get(resources, ['data', resourceId], {});
  const organizationId = useSelector(selectAppId);
  const slugAppScope = myServices.appScopeService.useSlugAppScope();
  const isEnterprise = rd.getOrElse(
    myServices.featureService.useFeature(
      myServices.appScopeService.useOrganizationScopeIdentity(),
      'enterprise'
    ),
    false
  );
  // Capture here — GenerateResourcePDFModal Portal is outside nested AppScopeProvider.
  const browsingScopeRd = rd.map(
    myServices.appScopeService.useResolvedAppScope(),
    appScopeUtils.identityFromResolved
  );
  const { t } = useTranslation('resources', {
    keyPrefix: 'AssignResourcesToWorkspacesDialog'
  });

  const { openModal: openNewModal } = useModal();

  const openGeneratePDFModal = () => {
    rd.mapOrElse(
      browsingScopeRd,
      browsingScope => {
        openNewModal(() => (
          <GenerateResourcePDFModal
            resourceId={resource.id}
            browsingScope={browsingScope}
          />
        ));
      },
      undefined
    );
  };
  const confirmUnarchive = () => {
    const options = {
      title: 'Are you sure you want to unarchive this resource?',
      button: 'Unarchive Resource',
      action: 'UNARCHIVE_CONTACT',
      contact_id: resourceId
    };
    dispatch(openModal('ConfirmationModal', options));
  };

  const confirmArchive = () => {
    const options = {
      title: 'Are you sure you want to archive this resource?',
      text: 'You cannot undo this action',
      button: 'Archive Resource',
      action: 'DELETE_CONTACT',
      contact_id: resourceId
    };
    dispatch(openModal('ConfirmationModal', options));
  };

  return (
    <div className="tailwind-root contents">
      <RestrictedDropdownV5
        capabilitiesAtLeastOne={[
          Capability.RESOURCE_ARCHIVE,
          Capability.RESOURCE_UPDATE,
          Capability.RESOURCE_READ
        ]}
        trigger={
          <Button
            variant="secondary"
            size="sm"
            layout="icon"
            aria-label="Resource actions"
          >
            <MoreHorizontal />
          </Button>
        }
        align="end"
      >
        {!excludeOptions.includes('generate-pdf') && (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_READ}>
            <DropdownMenuItem size="lg" onSelect={openGeneratePDFModal}>
              <FileText />
              Generate PDF
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        )}
        {isEnterprise && (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_UPDATE}>
            <DropdownMenuItem
              size="lg"
              onSelect={() => {
                myServices.userFlowService.openAssignResourcesToWorkspacesDialog(
                  {
                    organizationId,
                    resourceIds: [resourceId],
                    resourceName:
                      resource.full_name || `Resource ${resourceId}`,
                    initialWorkspaceIds: (
                      resource.enterprise_workspaces ?? []
                    ).map(entry => entry.enterprise_workspace_id),
                    appScope: slugAppScope
                  }
                );
              }}
            >
              <Building2 />
              {t('title')}
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        )}
        {!excludeOptions.includes('request-availability') && (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_UPDATE}>
            <DropdownMenuItem
              onSelect={() => {
                myServices.userFlowService.openRequestAvailabilityDialog({
                  resourceIds: [resourceId],
                  appScope: slugAppScope
                });
              }}
              size="lg"
            >
              <img src="/images/icon_availability_request.svg" alt="" />
              Request availability
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        )}
        {!excludeOptions.includes('add-availability') && (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_UPDATE}>
            <DropdownMenuItem
              onSelect={() => {
                dispatch(
                  openModal('ResourceEventsModal', {
                    resourceId
                  })
                );
              }}
              size="lg"
            >
              <img src="/images/icon_availability.svg" alt="" />
              Add availability / booking
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        )}
        {resource.archived ? (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_UPDATE}>
            <DropdownMenuItem onSelect={confirmUnarchive} size="lg">
              <img src="/images/icon_delete.svg" alt="" />
              Unarchive contact
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        ) : (
          <></>
        )}
        {!resource.archived ? (
          <RestrictedDropdownItem capabilities={Capability.RESOURCE_ARCHIVE}>
            <DropdownMenuItem onSelect={confirmArchive} size="lg">
              <Trash2 className="text-fg-danger" />
              Archive contact
            </DropdownMenuItem>
          </RestrictedDropdownItem>
        ) : (
          // This is to stop a hissy fit
          <></>
        )}
      </RestrictedDropdownV5>
    </div>
  );
};

export default ResourceOptions;
